<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TemplateEditorDialog from 'dashboard/routes/dashboard/campaigns/pages/templates/TemplateEditorDialog.vue';

const store = useStore();
const { t } = useI18n();

const isLoading = computed(
  () => store.getters['customMessageTemplates/getUIFlags'].isFetching
);
const templates = computed(
  () => store.getters['customMessageTemplates/getActiveTemplates'] || []
);
const inboxes = computed(() => store.getters['inboxes/getWhatsAppInboxes']);

const showEditor = ref(false);
const editingTemplate = ref(null);
const reviewingTemplate = ref(null);
const isAdministrator = computed(
  () => store.getters.getCurrentUser?.role === 'administrator'
);

const searchQuery = ref('');
const statusFilter = ref('all');

const fetchTemplates = async () => {
  try {
    await store.dispatch('customMessageTemplates/get');
    if (!inboxes.value.length) {
      await store.dispatch('inboxes/get');
    }
    await store.dispatch('teams/get');
    // eslint-disable-next-line no-use-before-define
    await syncTemplateStatuses();
  } catch (error) {
    useAlert(t('ACTIVE_MESSAGES.ERRORS.LOAD'));
  }
};

const syncTemplateStatuses = async () => {
  const officialTemplates = templates.value.filter(
    template => template.inbox_id
  );

  await Promise.all(
    officialTemplates.map(template =>
      store
        .dispatch('customMessageTemplates/syncStatus', template.id)
        .catch(() => null)
    )
  );
};

const handleCreateTemplate = () => {
  editingTemplate.value = { category: 'utility' };
  showEditor.value = true;
};

const handleEditTemplate = template => {
  editingTemplate.value = template;
  showEditor.value = true;
};

const openReview = template => {
  reviewingTemplate.value = template;
};

const closeReview = () => {
  reviewingTemplate.value = null;
};

const releaseReview = async () => {
  if (!reviewingTemplate.value || !isAdministrator.value) return;

  try {
    await store.dispatch(
      'customMessageTemplates/releaseReview',
      reviewingTemplate.value.id
    );
    useAlert(t('ACTIVE_MESSAGES.SUCCESS.RELEASED'));
    closeReview();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('ACTIVE_MESSAGES.ERRORS.RELEASE')
    );
  }
};

const removeReview = async () => {
  if (!reviewingTemplate.value || !isAdministrator.value) return;
  // Confirmation is required before removing a message template.
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(t('ACTIVE_MESSAGES.CONFIRM_DELETE'))
  ) {
    return;
  }

  try {
    await store.dispatch(
      'customMessageTemplates/destroyReview',
      reviewingTemplate.value.id
    );
    useAlert(t('ACTIVE_MESSAGES.SUCCESS.REMOVED'));
    closeReview();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('ACTIVE_MESSAGES.ERRORS.REMOVE')
    );
  }
};

const handleCopyTemplate = template => {
  editingTemplate.value = {
    ...template,
    id: null,
    name: `${template.name} (${t('ACTIVE_MESSAGES.COPY_SUFFIX')})`,
  };
  showEditor.value = true;
};

const templateToActOn = ref(null);
const showActionModal = ref(false);
const actionType = ref(null);

const handleDeactivateTemplate = template => {
  templateToActOn.value = template;
  actionType.value = 'deactivate';
  showActionModal.value = true;
};

const handleDeleteTemplate = template => {
  templateToActOn.value = template;
  actionType.value = 'delete';
  showActionModal.value = true;
};

const confirmAction = async () => {
  if (!templateToActOn.value) return;

  if (actionType.value === 'deactivate') {
    try {
      const payload = {
        id: templateToActOn.value.id,
        variables: {
          allowed_in_chat: false,
        },
      };

      await store.dispatch('customMessageTemplates/update', payload);
      useAlert(t('ACTIVE_MESSAGES.SUCCESS.DEACTIVATED'));
    } catch (error) {
      useAlert(
        error?.response?.data?.error || t('ACTIVE_MESSAGES.ERRORS.DEACTIVATE')
      );
    }
  } else if (actionType.value === 'delete') {
    try {
      await store.dispatch(
        'customMessageTemplates/delete',
        templateToActOn.value.id
      );
      useAlert(t('ACTIVE_MESSAGES.SUCCESS.DELETED'));
    } catch (error) {
      useAlert(t('ACTIVE_MESSAGES.ERRORS.DELETE'));
    }
  }

  templateToActOn.value = null;
  showActionModal.value = false;
};

const filteredTemplates = computed(() => {
  return templates.value.filter(tpl => {
    const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');
    const matchesSearch =
      String(tpl.name || '')
        .toLocaleLowerCase('pt-BR')
        .includes(query) ||
      String(tpl.content || '')
        .toLocaleLowerCase('pt-BR')
        .includes(query);
    const effectiveStatus = tpl.status || 'approved';
    const matchesStatus =
      statusFilter.value === 'all' || effectiveStatus === statusFilter.value;
    const allowed = tpl.variables?.allowed_in_chat;
    const matchesActive = allowed === true || allowed === 'true';
    return matchesSearch && matchesStatus && matchesActive;
  });
});

const getInboxName = inboxId => {
  if (!inboxId) return t('ACTIVE_MESSAGES.ALL_INBOXES');
  const inbox = inboxes.value.find(i => i.id === inboxId);
  return inbox ? inbox.name : t('ACTIVE_MESSAGES.DELETED_INBOX');
};

const getTeamName = teamId => {
  if (!teamId) return t('ACTIVE_MESSAGES.GLOBAL_TEAM');
  const team = store.getters['teams/getTeams']?.find(
    teamItem => teamItem.id === teamId
  );
  return team ? team.name : t('ACTIVE_MESSAGES.UNKNOWN_TEAM');
};

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const isExclusive = tpl => {
  return (
    tpl.variables?.is_exclusive_active_message === true ||
    tpl.variables?.is_exclusive_active_message === 'true'
  );
};

const handleCardKeydown = (event, template) => {
  if (!isExclusive(template)) return;
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleEditTemplate(template);
  }
};

onMounted(() => {
  fetchTemplates();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, vue/no-static-inline-styles, vue/html-self-closing -->
  <CampaignLayout
    :header-title="t('ACTIVE_MESSAGES.TITLE')"
    :button-label="t('ACTIVE_MESSAGES.NEW_MESSAGE')"
    @click="handleCreateTemplate"
  >
    <template #action>
      <TemplateEditorDialog
        v-if="showEditor"
        :template="editingTemplate"
        context="active_messages"
        @close="
          showEditor = false;
          fetchTemplates();
        "
      />

      <woot-delete-modal
        v-model:show="showActionModal"
        :on-close="
          () => {
            templateToActOn = null;
            showActionModal = false;
          }
        "
        :on-confirm="confirmAction"
        :title="
          actionType === 'delete'
            ? t('ACTIVE_MESSAGES.DELETE_TITLE')
            : t('ACTIVE_MESSAGES.DEACTIVATE_TITLE')
        "
        :message="
          actionType === 'delete'
            ? t('ACTIVE_MESSAGES.CONFIRM_DELETE_MESSAGE')
            : t('ACTIVE_MESSAGES.CONFIRM_DEACTIVATE')
        "
        :confirm-text="
          actionType === 'delete'
            ? t('COMMON.DELETE')
            : t('ACTIVE_MESSAGES.REMOVE_FROM_CHAT')
        "
        :reject-text="t('COMMON.CANCEL')"
      />

      <div
        v-if="reviewingTemplate && isAdministrator"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="active-meta-review-title"
        @click.self="closeReview"
      >
        <div
          class="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-800"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2
                id="active-meta-review-title"
                class="text-lg font-semibold text-slate-900 dark:text-slate-100"
              >
                {{ t('ACTIVE_MESSAGES.ADMIN_REVIEW') }}
              </h2>
              <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {{ reviewingTemplate.name }}
              </p>
            </div>
            <button
              type="button"
              class="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              :aria-label="t('COMMON.CLOSE')"
              @click="closeReview"
            >
              <span class="icon i-lucide-x" />
            </button>
          </div>

          <div
            class="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
          >
            <p class="font-semibold">
              {{ t('ACTIVE_MESSAGES.META_RECLASSIFIED') }}
            </p>
            <p class="mt-2">
              {{ t('ACTIVE_MESSAGES.MARKETING_WARNING') }}
            </p>
            <dl class="mt-3 space-y-1 text-xs">
              <div class="flex justify-between gap-4">
                <dt>{{ t('ACTIVE_MESSAGES.SUBMITTED_CATEGORY') }}</dt>
                <dd class="font-semibold">
                  {{
                    reviewingTemplate.variables?.meta_submitted_category || '—'
                  }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt>{{ t('ACTIVE_MESSAGES.META_CATEGORY') }}</dt>
                <dd class="font-semibold">
                  {{
                    reviewingTemplate.variables?.meta_category || 'Marketing'
                  }}
                </dd>
              </div>
            </dl>
          </div>

          <div class="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              class="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/30"
              @click="removeReview"
            >
              {{ t('ACTIVE_MESSAGES.DELETE_TEMPLATE') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              @click="releaseReview"
            >
              {{ t('ACTIVE_MESSAGES.RELEASE_TEMPLATE') }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <div
      v-if="isLoading"
      class="flex justify-center p-8"
      role="status"
      :aria-label="t('ACTIVE_MESSAGES.LOADING')"
    >
      <Spinner />
    </div>

    <div
      v-else-if="templates.length === 0"
      class="flex flex-col items-center justify-center p-12 text-slate-500 dark:text-slate-400 dark:text-slate-500"
    >
      <div
        class="i-lucide-message-square text-6xl text-slate-300 dark:text-slate-600 mb-4"
      />
      <h3 class="text-xl font-medium mb-2">
        {{ t('ACTIVE_MESSAGES.NO_TEMPLATES') }}
      </h3>
      <p class="text-center text-sm mb-6 max-w-md">
        {{ t('ACTIVE_MESSAGES.NO_TEMPLATES_DESCRIPTION') }}
      </p>
      <Button
        :label="t('ACTIVE_MESSAGES.CREATE_FIRST')"
        icon="i-lucide-plus"
        @click="handleCreateTemplate"
      />
    </div>

    <div v-else class="flex flex-col gap-6">
      <!-- Filtros -->
      <div
        class="flex flex-col gap-3 bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 sm:flex-row sm:items-center sm:gap-4"
      >
        <div class="relative w-full flex-1 sm:max-w-md">
          <Input
            v-model="searchQuery"
            :aria-label="t('ACTIVE_MESSAGES.SEARCH_LABEL')"
            :placeholder="t('ACTIVE_MESSAGES.SEARCH_PLACEHOLDER')"
            class="!mb-0"
          />
          <div
            class="absolute right-3 top-1/2 -translate-y-1/2 i-lucide-search text-slate-400 dark:text-slate-500"
          />
        </div>
        <div class="relative w-full sm:w-48">
          <select
            v-model="statusFilter"
            :aria-label="t('ACTIVE_MESSAGES.FILTER_LABEL')"
            class="appearance-none bg-none block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-slate-50 dark:bg-slate-900/50 py-[9px] pl-3 pr-10 outline-slate-200 focus:outline-slate-300 text-slate-700 dark:text-slate-200 cursor-pointer"
          >
            <option value="all">{{ t('ACTIVE_MESSAGES.ALL_STATUS') }}</option>
            <option value="approved">
              {{ t('ACTIVE_MESSAGES.APPROVED') }}
            </option>
            <option value="pending">{{ t('ACTIVE_MESSAGES.PENDING') }}</option>
            <option value="rejected">
              {{ t('ACTIVE_MESSAGES.REJECTED') }}
            </option>
            <option value="awaiting_approval">
              {{ t('ACTIVE_MESSAGES.AWAITING_APPROVAL') }}
            </option>
          </select>
          <div
            class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
          >
            <div class="i-lucide-chevron-down size-4 text-slate-500" />
          </div>
        </div>
      </div>

      <!-- Lista de Modelos -->
      <div
        v-if="filteredTemplates.length === 0"
        class="text-center text-slate-500 dark:text-slate-400 dark:text-slate-500 py-8"
      >
        <span role="status" aria-live="polite">
          {{ t('ACTIVE_MESSAGES.NO_FILTER_RESULTS') }}
        </span>
      </div>

      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <div
          v-for="tpl in filteredTemplates"
          :key="tpl.id"
          class="border border-slate-200 dark:border-slate-700 rounded-xl hover:shadow-md bg-white dark:bg-slate-800 transition-all flex flex-col overflow-hidden relative group"
          :class="isExclusive(tpl) ? 'cursor-pointer' : 'cursor-default'"
          :role="isExclusive(tpl) ? 'button' : undefined"
          :tabindex="isExclusive(tpl) ? 0 : undefined"
          :aria-label="
            isExclusive(tpl)
              ? t('ACTIVE_MESSAGES.EDIT_MESSAGE_ARIA', {
                  name: tpl.name || t('ACTIVE_MESSAGES.NO_NAME'),
                })
              : undefined
          "
          @click="isExclusive(tpl) ? handleEditTemplate(tpl) : null"
          @keydown="handleCardKeydown($event, tpl)"
        >
          <div class="p-4 flex-1 relative">
            <div class="flex justify-between items-start mb-2 gap-2 min-h-7">
              <h4
                class="font-bold text-slate-900 dark:text-slate-100 truncate flex-1"
              >
                {{ tpl.name }}
              </h4>

              <div class="relative">
                <!-- Status (Oculto no hover) -->
                <div
                  class="transition-opacity duration-200 group-hover:opacity-0 group-focus-within:opacity-0"
                >
                  <div
                    v-if="tpl.status === 'awaiting_approval'"
                    class="flex items-center gap-2"
                  >
                    <span
                      class="inline-flex h-6 items-center text-[11px] px-2 bg-yellow-100 text-yellow-800 rounded-full flex-shrink-0 font-medium border border-yellow-200 dark:bg-yellow-900/70 dark:text-yellow-200 dark:border-yellow-700"
                    >
                      {{ t('ACTIVE_MESSAGES.STATUS_AWAITING') }}
                    </span>
                  </div>
                  <div
                    v-else-if="tpl.status === 'approved' || !tpl.status"
                    class="text-[11px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex-shrink-0 font-medium border border-green-200"
                  >
                    {{ t('ACTIVE_MESSAGES.STATUS_APPROVED') }}
                  </div>
                  <div
                    v-else-if="tpl.status === 'pending'"
                    class="text-[11px] px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full flex-shrink-0 font-medium border border-yellow-200"
                  >
                    {{ t('ACTIVE_MESSAGES.STATUS_PENDING') }}
                  </div>
                  <div
                    v-else-if="tpl.status === 'rejected'"
                    class="text-[11px] px-2 py-0.5 bg-red-100 text-red-700 rounded-full flex-shrink-0 font-medium border border-red-200"
                  >
                    {{ t('ACTIVE_MESSAGES.STATUS_REJECTED') }}
                  </div>
                </div>

                <!-- Botões (Exibidos no hover) -->
                <div
                  class="mt-2 flex gap-1.5 opacity-100 transition-opacity duration-200 sm:absolute sm:top-0 sm:right-0 sm:mt-0 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                >
                  <button
                    v-if="isExclusive(tpl)"
                    class="flex items-center justify-center p-1.5 bg-slate-100 text-slate-600 hover:bg-slate-600 hover:text-white rounded-md transition-colors dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                    :title="t('COMMON.EDIT')"
                    :aria-label="
                      t('ACTIVE_MESSAGES.EDIT_ARIA', {
                        name: tpl.name || t('ACTIVE_MESSAGES.ACTIVE_MESSAGE'),
                      })
                    "
                    @click.stop="handleEditTemplate(tpl)"
                  >
                    <div class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    class="flex items-center justify-center p-1.5 bg-blue-50 text-blue-500 hover:bg-blue-500 hover:text-white rounded-md transition-colors"
                    :title="t('ACTIVE_MESSAGES.DUPLICATE')"
                    :aria-label="
                      t('ACTIVE_MESSAGES.DUPLICATE_ARIA', {
                        name: tpl.name || t('ACTIVE_MESSAGES.ACTIVE_MESSAGE'),
                      })
                    "
                    @click.stop="handleCopyTemplate(tpl)"
                  >
                    <div class="i-lucide-copy size-4" />
                  </button>
                  <button
                    v-if="!isExclusive(tpl)"
                    class="flex items-center justify-center p-1.5 bg-orange-50 text-orange-500 hover:bg-orange-500 hover:text-white rounded-md transition-colors"
                    :title="t('ACTIVE_MESSAGES.UNLINK_CHAT')"
                    :aria-label="
                      t('ACTIVE_MESSAGES.UNLINK_ARIA', {
                        name: tpl.name || t('ACTIVE_MESSAGES.MESSAGE'),
                      })
                    "
                    @click.stop="handleDeactivateTemplate(tpl)"
                  >
                    <div class="i-lucide-eye-off size-4" />
                  </button>
                  <button
                    v-if="isExclusive(tpl)"
                    class="flex items-center justify-center p-1.5 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-md transition-colors"
                    :title="t('COMMON.DELETE')"
                    :aria-label="
                      t('ACTIVE_MESSAGES.DELETE_ARIA', {
                        name: tpl.name || t('ACTIVE_MESSAGES.ACTIVE_MESSAGE'),
                      })
                    "
                    @click.stop="handleDeleteTemplate(tpl)"
                  >
                    <div class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-2 mb-3">
              <span
                class="text-[10px] uppercase font-bold tracking-wider px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 rounded"
                >{{ tpl.category }}</span
              >
              <div
                class="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-300 truncate bg-slate-50 dark:bg-slate-900/50 px-2 py-1 rounded border border-slate-100 dark:border-slate-700/50"
              >
                <div class="i-lucide-inbox size-3" />
                <span class="truncate">{{ getInboxName(tpl.inbox_id) }}</span>
              </div>
              <div
                v-if="tpl.team_id"
                class="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-300 truncate bg-indigo-50/50 dark:bg-indigo-900/30 px-2 py-1 rounded border border-indigo-100 dark:border-indigo-800"
              >
                <div class="i-lucide-users size-3" />
                <span class="truncate">{{ getTeamName(tpl.team_id) }}</span>
              </div>
            </div>
            <p
              class="text-sm text-slate-600 dark:text-slate-300 line-clamp-3 leading-relaxed"
            >
              {{ tpl.content }}
            </p>
            <div
              v-if="tpl.status === 'awaiting_approval' && isAdministrator"
              class="mt-3 flex justify-end"
            >
              <button
                type="button"
                class="inline-flex h-7 items-center rounded-md bg-amber-500 px-3 text-xs font-semibold text-white shadow-sm hover:bg-amber-600 dark:bg-amber-500 dark:hover:bg-amber-400"
                @click.stop="openReview(tpl)"
              >
                {{ t('ACTIVE_MESSAGES.REVIEW_TEMPLATE') }}
              </button>
            </div>
          </div>

          <div
            class="bg-slate-50 dark:bg-slate-900/50 px-4 py-2 border-t border-slate-100 dark:border-slate-700/50 flex justify-between items-center text-[11px] text-slate-400 dark:text-slate-500"
          >
            <div class="flex items-center gap-1">
              <div class="i-lucide-calendar size-3" />
              <span>{{ formatDate(tpl.created_at) }}</span>
            </div>
            <div class="flex items-center gap-1">
              <div class="i-lucide-bar-chart-2 size-3" />
              <span>{{ t('ACTIVE_MESSAGES.SEND_COUNT', { count: 0 }) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </CampaignLayout>
</template>
